import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { CloseIcon } from "@/components/icons/close-icon";
import { useGetOtherPreferences } from "@/hooks/requests/other-preferences/use-get-other-preferences";
import { useRemoveMealFromSlot } from "@/hooks/requests/plan/use-remove-meal-from-slot";
import { useToast } from "@/hooks/ui/use-toast";
import { MealPlan } from "@/pages/plans/types/meal-plan.type";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMealDetailData } from "../../hooks/use-meal-detail-data";
import { useMyPlanAnalyticsContext } from "../../hooks/use-my-plan-analytics";
import { useSwipeDistance } from "../../hooks/use-swipe-distance";
import { isFruitMeal } from "../../utils/is-fruit-meal";
import { RemoveMealConfirmModal } from "../remove-meal-confirm-modal";
import { CarbLabel } from "./carb-label";
import { FruitContent } from "./fruit-content";
import { MealCardAction } from "./meal-card-action";
import { MealCoachButton } from "./meal-coach-button";
import { MealTitle } from "./meal-title";
import { useSwipe } from "./use-swipe";

interface MealCardProps {
  meal: Meal;
  mealTime: MealTime;
  mealDay: string;
  targetDate?: string;
  mealIndex: number;
  isMyFood?: boolean;
  disableSwap?: boolean;
  isTickedOff: boolean;
  isExtraMeal?: boolean;
  /**
   * `"menu"` (default) renders the legacy Swap/Repeat/Remove context menu.
   * `"coach"` (My Plan v2) replaces it with the NutriCoach sparkle button and
   * keeps ticked meals inline with a done style.
   */
  actionVariant?: "menu" | "coach";
}

const mealChangeMap = new Map<string, string>();

export const MealCard = memo(function MealCard({
  meal,
  mealTime,
  mealDay,
  targetDate,
  mealIndex,
  disableSwap = false,
  isTickedOff = false,
  isExtraMeal = false,
  actionVariant = "menu",
}: MealCardProps) {
  const isCoachVariant = actionVariant === "coach";
  // In the coach variant ticked meals stay inline with a done style instead
  // of moving to a separate "Eaten" section.
  const isDone = isCoachVariant && isTickedOff;
  const { onMealDetailClick } = useMealDetailData();
  const myPlanAnalytics = useMyPlanAnalyticsContext();
  const { removeMealFromSlotAsync, isLoading: isRemoving } = useRemoveMealFromSlot();
  const { showSuccess, showError } = useToast();
  const { otherPreferences } = useGetOtherPreferences();
  const showCaloriesAndMacros = otherPreferences?.showCaloriesAndMacros ?? false;
  const ignoreClickRef = useRef(false);
  const [isUpdated, setIsUpdated] = useState(false);
  // Drives the remove-reveal panel shown behind the card while dragging left.
  const [isSwipingLeft, setIsSwipingLeft] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  const changeKey = useMemo(
    () => `${mealDay}-${mealTime}-${mealIndex}`,
    [mealDay, mealTime, mealIndex],
  );

  useEffect(() => {
    const currentId = meal.id ?? "";
    const previousId = mealChangeMap.get(changeKey);
    const hasChanged = Boolean(previousId && previousId !== currentId);
    setIsUpdated(hasChanged);
    mealChangeMap.set(changeKey, currentId);
  }, [changeKey, meal.id]);

  // Reset isUpdated to false after 5 seconds
  useEffect(() => {
    if (isUpdated) {
      const timer = setTimeout(() => {
        setIsUpdated(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isUpdated]);

  const handleOpenDetail = () => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    myPlanAnalytics?.trackInteraction("meal_card_opened", {
      meal_time: mealTime,
      has_meal: true,
      is_ticked_off: isTickedOff,
    });
    const mealPlan: MealPlan = {
      meal,
      mealTime,
      mealDay,
      targetDate,
      mealIndex,
      fromPlan: true,
      isExtraMeal,
    };
    onMealDetailClick(mealPlan);
  };

  const handleSwipeLeft = useCallback(() => {
    ignoreClickRef.current = true;
    myPlanAnalytics?.trackInteraction("remove_meal_started", {
      meal_time: mealTime,
      has_meal: true,
      is_ticked_off: isTickedOff,
    });
    setIsRemoveConfirmOpen(true);
  }, [isTickedOff, mealTime, myPlanAnalytics]);

  const handleConfirmRemove = useCallback(async () => {
    try {
      await removeMealFromSlotAsync({
        day: mealDay,
        mealTime,
        meal_id: meal.id ?? "",
        targetDate: targetDate ?? undefined,
      });
      showSuccess("Meal removed");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to remove meal. Please try again.");
    } finally {
      setIsRemoveConfirmOpen(false);
    }
  }, [meal.id, mealDay, mealTime, targetDate, removeMealFromSlotAsync, showSuccess, showError]);

  const handleSwipeMove = useCallback((deltaX: number) => {
    setIsSwipingLeft(deltaX < 0);
  }, []);

  const handleSwipeEnd = useCallback(() => {
    setIsSwipingLeft(false);
  }, []);

  const swipeDistance = useSwipeDistance();

  const { ref: swipeRef } = useSwipe({
    // A ticked-off meal has already been eaten, so it cannot be removed —
    // withholding the handler disables the gesture and its reveal panel.
    onSwipeLeft: disableSwap || isTickedOff ? undefined : handleSwipeLeft,
    onSwipeMove: handleSwipeMove,
    onSwipeEnd: handleSwipeEnd,
    threshold: swipeDistance / 3,
    velocityThreshold: 0.5,
    maxTranslate: swipeDistance,
    disabled: isRemoving,
  });

  const isFruit = isFruitMeal(meal);

  return (
    <div className={cn("shadow-meal-card relative rounded-2xl")}>
      {isUpdated && (
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-black" />
      )}
      {/* Remove reveal — shown while dragging left (right-aligned). */}
      {isSwipingLeft && (
        <div
          className={cn(
            "absolute inset-0 m-px flex items-center justify-end rounded-2xl px-5 text-white",
            "bg-toast-error-default shadow-[inset_0_0_8px_0_rgba(0,0,0,0.32)]",
          )}
        >
          <div className="flex flex-col items-center gap-2">
            <CloseIcon className="h-5 w-5 fill-white" />
            <span className={cn(textVariants.footnote, "text-white")}>Remove</span>
          </div>
        </div>
      )}
      <div
        ref={swipeRef}
        onClick={handleOpenDetail}
        className={cn(
          "relative z-10 cursor-pointer rounded-2xl p-4 pr-2",
          "transition-all duration-200",
          "flex items-center justify-between gap-1 bg-white select-none",
          {
            "cursor-pointer hover:shadow-md": !isTickedOff,
          },
        )}
      >
        {isFruit ? (
          <FruitContent mealName={meal.name} isDone={isDone} />
        ) : (
          <div className="z-10 flex flex-1 flex-col gap-[14.5px] overflow-hidden">
            <div className="flex items-start gap-2">
              <MealTitle
                title={meal.name}
                className={cn("flex-1", isDone && "line-through decoration-solid opacity-50")}
              />
            </div>

            <div className={cn(isDone && "opacity-50")}>
              <CarbLabel
                hasCarbOption={meal.hasCarbOption}
                carbOption={meal.defaultCarbOption}
                portionSize={meal.portionSize?.toString() ?? "--"}
                productComplexity={meal.productComplexity}
                ingredientQuantities={meal.ingredientQuantities}
                nutritionalInfo={showCaloriesAndMacros ? meal.nutritionalInfo : undefined}
                ingredients={meal.ingredients}
              />
            </div>
          </div>
        )}

        <div className="relative h-11 w-11">
          {isCoachVariant ? (
            <MealCoachButton
              mealTime={mealTime}
              mealDay={mealDay}
              targetDate={targetDate}
              mealName={meal.name}
              mealId={meal.id}
            />
          ) : (
            <MealCardAction
              meal={meal}
              mealTime={mealTime}
              mealDay={mealDay}
              targetDate={targetDate}
              mealIndex={mealIndex}
              isTickedOff={isTickedOff}
              disabled={isRemoving}
              isExtraMeal={isExtraMeal}
            />
          )}
        </div>
      </div>

      <RemoveMealConfirmModal
        isOpen={isRemoveConfirmOpen}
        onConfirm={handleConfirmRemove}
        onCancel={() => setIsRemoveConfirmOpen(false)}
        isRemoving={isRemoving}
      />
    </div>
  );
});
